import React, { useEffect, useRef, useState } from 'react';
import { NavigationArrow } from '@phosphor-icons/react';
import { GOOGLE_MAPS_MAP_ID, loadGoogleMaps } from '../lib/googleMaps';
import { hasVenueCoordinates } from '../lib/locationLinks';

const DEFAULT_CENTER = { lat: 28.6139, lng: 77.209 };

const markerCoordinates = (position) => ({
  lat: typeof position?.lat === 'function' ? position.lat() : Number(position?.lat),
  lng: typeof position?.lng === 'function' ? position.lng() : Number(position?.lng),
});

export default function VenueMap({ value, fallbackVenue, onSelect, onError, busy }) {
  const [ready, setReady] = useState(false);
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const markerClassRef = useRef(null);
  const onSelectRef = useRef(onSelect);
  const onErrorRef = useRef(onError);
  const initialVenueRef = useRef(hasVenueCoordinates(value) ? value : fallbackVenue);

  useEffect(() => {
    onSelectRef.current = onSelect;
    onErrorRef.current = onError;
  }, [onError, onSelect]);

  useEffect(() => {
    if (!containerRef.current) return undefined;
    let disposed = false;
    const initialVenue = initialVenueRef.current;
    const initialCenter = hasVenueCoordinates(initialVenue)
      ? { lat: Number(initialVenue.lat), lng: Number(initialVenue.lng) }
      : DEFAULT_CENTER;

    const initialize = async () => {
      setReady(false);
      try {
        const { Map, AdvancedMarkerElement } = await loadGoogleMaps();
        if (disposed) return;
        const map = new Map(containerRef.current, {
          center: initialCenter,
          zoom: hasVenueCoordinates(initialVenue) ? 15 : 9,
          mapId: GOOGLE_MAPS_MAP_ID,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        });
        map.addListener('click', (event) => {
          const coordinates = markerCoordinates(event.latLng);
          onSelectRef.current?.(coordinates.lat, coordinates.lng);
        });
        markerClassRef.current = AdvancedMarkerElement;
        mapRef.current = map;
        setReady(true);
      } catch (error) {
        console.error('Google Maps initialization failed:', error);
        onErrorRef.current?.(
          `Google Maps could not be loaded${error?.message ? `: ${error.message}` : ''}. Venue details can still be entered manually.`
        );
      }
    };

    initialize();
    return () => {
      disposed = true;
      if (markerRef.current) markerRef.current.map = null;
      if (window.google?.maps && mapRef.current) {
        window.google.maps.event.clearInstanceListeners(mapRef.current);
      }
      markerRef.current = null;
      mapRef.current = null;
      markerClassRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!ready || !mapRef.current) return;
    const selectedVenue = { lat: value?.lat, lng: value?.lng, name: value?.name };
    if (!hasVenueCoordinates(selectedVenue)) {
      if (markerRef.current) markerRef.current.map = null;
      markerRef.current = null;
      return;
    }

    const position = { lat: Number(selectedVenue.lat), lng: Number(selectedVenue.lng) };
    if (!markerRef.current) {
      markerRef.current = new markerClassRef.current({
        map: mapRef.current,
        position,
        gmpDraggable: true,
        title: selectedVenue.name || 'Selected venue',
      });
      markerRef.current.addListener('dragend', () => {
        const coordinates = markerCoordinates(markerRef.current.position);
        onSelectRef.current?.(coordinates.lat, coordinates.lng);
      });
    } else {
      markerRef.current.position = position;
    }
    mapRef.current.panTo(position);
    mapRef.current.setZoom(15);
  }, [ready, value?.lat, value?.lng, value?.name]);

  return (
    <div className="relative">
      <div ref={containerRef} className="h-52 w-full rounded-lg border border-gray-200 bg-slate-100" data-testid="venue-map" />
      <div className="pointer-events-none absolute bottom-2 left-2 rounded bg-white/90 px-2 py-1 text-xs shadow">
        <NavigationArrow className="mr-1 inline h-3 w-3" />
        {busy ? 'Finding address...' : 'Click or drag the pin to adjust'}
      </div>
    </div>
  );
}
